import styled from "@emotion/styled/macro";
import {
  Button,
  MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
  tokens,
} from "@sunrun/experience-ui-components";
import shareIcon from "components/assets/images/download-icon.svg";
import { formatCurrency } from "utils/formatHelpers";
import { OfferReviewSimplifiedPaymentDisclaimer } from "components/organisms/OfferReviewSimplifiedPaymentDisclaimer";
import { SIMPLIFIED_OFFER_REVIEW_PAYMENT_DISCLAIMER } from "components/organisms/offerReviewPayment/offerReviewPaymentUtils";

type FlexActionBarPayment = {
  primaryLine: string;
  secondaryLine: string;
};

type OfferReviewSimplifiedActionBarProps = {
  monthlyPayment?: number;
  dueAtSigning: number;
  isPrepaid?: boolean;
  totalAmount?: number;
  flexPayment?: FlexActionBarPayment | null;
  onCheckoutClick: () => void;
  checkoutDisabled?: boolean;
  onUtilityBillClick: () => void;
  onShareClick: () => void;
  isCustomer: boolean;
  isNewHomes: boolean;
  hasUploadedUtilityBill: boolean;
  embedded?: boolean;
  offerExpires: string;
  isMobile: boolean;
};

const OfferReviewSimplifiedActionBar = ({
  monthlyPayment,
  dueAtSigning,
  isPrepaid = false,
  totalAmount,
  flexPayment,
  onCheckoutClick,
  checkoutDisabled,
  onUtilityBillClick,
  onShareClick,
  isCustomer,
  isNewHomes,
  hasUploadedUtilityBill,
  embedded = false,
  offerExpires,
  isMobile,
}: OfferReviewSimplifiedActionBarProps) => {
  const isFlexBar = flexPayment != null;
  const showUtilityBillButton = !isNewHomes && !hasUploadedUtilityBill;
  const showShareButton = !isCustomer;
  const showMonthlyPayment =
    !isFlexBar && !isPrepaid && monthlyPayment !== undefined;

  return (
    <Outer
      data-testid="offer-review-simplified-action-bar"
      $embedded={embedded}
    >
      <Bar $isFlex={isFlexBar}>
        <Heading $stacked={isFlexBar || isPrepaid}>
          {isFlexBar || isPrepaid ? (
            <>
              <span>Take control of powering</span>
              <span>your home today</span>
            </>
          ) : (
            "Take control of powering your home today"
          )}
        </Heading>
        <Trailing>
          {isFlexBar ? (
            <FlexPaymentBlock>
              <FlexPaymentPrimary>{flexPayment.primaryLine}</FlexPaymentPrimary>
              <FlexPaymentSecondary>
                {flexPayment.secondaryLine}
              </FlexPaymentSecondary>
            </FlexPaymentBlock>
          ) : isPrepaid ? (
            <PaymentBlock>
              <PaymentPrimary>
                Total Amount: {formatCurrency(totalAmount ?? 0, 0)}
              </PaymentPrimary>
            </PaymentBlock>
          ) : (
            <PaymentBlock>
              {showMonthlyPayment && (
                <PaymentPrimary>
                  {formatCurrency(monthlyPayment, 2)}
                  <PaymentMo>/mo</PaymentMo>
                </PaymentPrimary>
              )}
              <PaymentSecondary>
                {formatCurrency(dueAtSigning, 0)} due at signing
              </PaymentSecondary>
            </PaymentBlock>
          )}
          {showUtilityBillButton && (
            <UtilityBillButton
              color="secondary"
              size="sm"
              onClick={onUtilityBillClick}
              data-testid="offer-review-simplified-utility-bill"
            >
              Utility Bill
            </UtilityBillButton>
          )}
          {showShareButton && (
            <ShareButton
              color="secondary"
              size="sm"
              onClick={onShareClick}
              aria-label="Share offer"
              data-testid="offer-review-simplified-share"
              iconMiddle={<ShareIconImg src={shareIcon} alt="" />}
            />
          )}
          <NextButton
            color="primary"
            size="sm"
            onClick={onCheckoutClick}
            disabled={checkoutDisabled}
            data-testid="offer-review-simplified-next"
          >
            {isFlexBar ? "NEXT" : "Next"}
          </NextButton>
        </Trailing>
      </Bar>
      <OfferReviewSimplifiedPaymentDisclaimer
        isMobile={isMobile}
        disclaimer={SIMPLIFIED_OFFER_REVIEW_PAYMENT_DISCLAIMER}
        offerExpires={offerExpires}
      />
    </Outer>
  );
};

const Outer = styled.section<{ $embedded?: boolean }>`
  box-sizing: border-box;
  max-width: 1200px;
  width: ${({ $embedded }) => ($embedded ? "100%" : "calc(100% - 128px)")};
  margin: 0 auto ${({ $embedded }) => ($embedded ? "0" : "48px")};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: ${({ $embedded }) => ($embedded ? "100%" : "calc(100% - 32px)")};
  }
`;

const Bar = styled.div<{ $isFlex?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  min-height: 74px;
  padding: 12px 24px 12px 32px;
  border-radius: 92px;
  background: ${tokens.TINTS_HERO_BLUE_30};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
    padding: 16px;
    gap: 12px;
  }
`;

const Heading = styled.p<{ $stacked?: boolean }>`
  margin: 0;
  color: ${tokens.WHITE};
  font-size: 24px;
  font-weight: 500;
  line-height: ${({ $stacked }) => ($stacked ? "1.1" : "1.2")};
  letter-spacing: -0.02em;
  flex-shrink: 0;
  display: ${({ $stacked }) => ($stacked ? "flex" : "block")};
  flex-direction: ${({ $stacked }) => ($stacked ? "column" : "unset")};

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    text-align: center;
    align-items: center;
  }
`;

const Trailing = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const PaymentBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: ${tokens.WHITE};
  line-height: 1.1;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    align-items: center;
  }
`;

const PaymentPrimary = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0;
  font-size: 24px;
  font-weight: 500;
  letter-spacing: -0.02em;
  white-space: nowrap;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    font-size: 24px;
  }
`;

const PaymentMo = styled.span`
  font-size: 0.72em;
  font-weight: 500;
`;

const PaymentSecondary = styled.span`
  font-size: 13px;
  font-weight: 400;
  opacity: 0.95;
  margin-top: 2px;
`;

const FlexPaymentBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  color: ${tokens.WHITE};
  line-height: 1.1;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    align-items: center;
  }
`;

const FlexPaymentPrimary = styled.div`
  font-size: 15px;
  font-weight: 500;
  line-height: 1.35;
  text-align: right;
  white-space: nowrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    text-align: center;
    white-space: normal;
  }
`;

const FlexPaymentSecondary = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 1.35;
  text-align: right;
  opacity: 0.95;
  white-space: nowrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    text-align: center;
    white-space: normal;
  }
`;

const UtilityBillButton = styled(Button)`
  flex-shrink: 0;
  white-space: nowrap;

  &.sr-button-secondary {
    border-color: ${tokens.WHITE};
    color: ${tokens.WHITE};
  }

  &.sr-button-secondary > svg {
    fill: ${tokens.WHITE};
  }
`;

const ShareButton = styled(Button)`
  flex-shrink: 0;
  width: 37px;
  height: 37px;

  &.sr-button-secondary {
    border-color: ${tokens.WHITE};
  }
`;

const ShareIconImg = styled.img`
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
`;

const NextButton = styled(Button)`
  flex-shrink: 0;
  font-weight: 600;

  &.sr-button-primary {
    background: ${tokens.WHITE};
    color: ${tokens.BLACK};
  }

  &.sr-button-primary > svg {
    fill: ${tokens.BLACK};
  }

  &.sr-button-primary:not([disabled]):hover {
    background: ${tokens.TINTS_MEDIUM_BLUE_90};
  }
`;

export { OfferReviewSimplifiedActionBar };
export type { FlexActionBarPayment };
